/**
 * useTodayData — independent per-section loading for Vandaag (spec §8.1).
 *
 * Every section (planner, pinned notes, medication, caregiver messages) loads
 * through `repositories` on its own, so one failing source never blanks the
 * whole page:
 * - SUPABASE_NOT_CONFIGURED -> 'unavailable' (calm 'cloud niet ingesteld'
 *   mini-state in the section, page stays coherent);
 * - permission errors on optional sections -> 'unavailable' (section hides);
 * - other failures -> 'error' (compact retry note for core sections).
 *
 * Data refreshes when the page regains focus/visibility; silent refreshes keep
 * previously loaded content so offline staleness is shown, not a flash of
 * empty states.
 */
import { useCallback, useEffect, useState, type Dispatch, type SetStateAction } from 'react'
import { toAppError } from '@/lib/supabase/errors'
import { repositories } from '@/services/repositories'
import { detectLegacyLocalData, useSync, type LegacyLocalSummary } from '@/services/sync'
import type { AppNotification, MedicationOccurrence, Note, NotificationType, PlannerItem } from '@/types'
import { toLocalISODate } from './todayText'

export type SectionStatus = 'loading' | 'ready' | 'unavailable' | 'error'

export type SectionState<T> = {
  status: SectionStatus
  data: T
}

/** Notifications that represent a message from a caregiver to the client. */
export const CAREGIVER_MESSAGE_TYPES: readonly NotificationType[] = [
  'medication_scheduled',
  'caregiver_schedule_changed',
  'link_request',
]

const MAX_PINNED_NOTES = 3
const MAX_CAREGIVER_MESSAGES = 2

function loadingSection<T>(empty: T): SectionState<T> {
  return { status: 'loading', data: empty }
}

function statusForError(error: unknown): SectionStatus {
  const appError = toAppError(error, 'database')
  if (appError.code === 'SUPABASE_NOT_CONFIGURED' || appError.code === 'PERMISSION_DENIED') {
    return 'unavailable'
  }
  return 'error'
}

/** Pinned, non-archived notes, newest first, capped for the calm overview. */
export function pickPinnedNotes(notes: Note[], max: number = MAX_PINNED_NOTES): Note[] {
  return notes
    .filter((note) => note.pinned && !note.archived)
    .sort((a, b) => b.updatedAt.localeCompare(a.updatedAt))
    .slice(0, max)
}

/** Upcoming planner items for today: not yet finished, by start time. */
export function pickUpcomingPlannerItems(items: PlannerItem[], nowMinute: number, max = 3): PlannerItem[] {
  return [...items]
    .sort((a, b) => a.startMinute - b.startMinute)
    .filter((item) => item.startMinute + item.durationMinutes > nowMinute)
    .slice(0, max)
}

export function pickCaregiverMessages(notifications: AppNotification[], max = MAX_CAREGIVER_MESSAGES): AppNotification[] {
  return notifications
    .filter((notification) => CAREGIVER_MESSAGE_TYPES.includes(notification.type))
    .sort((a, b) => b.createdAt.localeCompare(a.createdAt))
    .slice(0, max)
}

export function useTodayData() {
  const { online, pendingWrites } = useSync()
  const [planner, setPlanner] = useState<SectionState<PlannerItem[]>>(() => loadingSection([]))
  const [pinnedNotes, setPinnedNotes] = useState<SectionState<Note[]>>(() => loadingSection([]))
  const [medicationOccurrences, setMedicationOccurrences] = useState<SectionState<MedicationOccurrence[]>>(() =>
    loadingSection([]),
  )
  const [caregiverMessages, setCaregiverMessages] = useState<SectionState<AppNotification[]>>(() =>
    loadingSection([]),
  )
  const [legacySummary, setLegacySummary] = useState<LegacyLocalSummary | null>(() => detectLegacyLocalData())
  const [refreshedAt, setRefreshedAt] = useState<Date | null>(null)

  const load = useCallback(async (silent: boolean) => {
    const today = toLocalISODate(new Date())

    const settle = <T,>(
      promise: Promise<T[]>,
      map: (value: T[]) => T[],
      apply: Dispatch<SetStateAction<SectionState<T[]>>>,
    ): Promise<void> =>
      promise
        .then((value) => {
          apply({ status: 'ready', data: map(value) })
        })
        .catch((error: unknown) => {
          apply((prev) => {
            // Silent refresh: keep the last good content visible (staleness is
            // communicated by the offline banner, not by clearing the page).
            if (silent && prev.status === 'ready') return prev
            return { status: statusForError(error), data: prev.data }
          })
        })

    await Promise.all([
      settle(repositories.planner.listForDate(today), (items) => items, setPlanner),
      settle(repositories.notes.list(false), (notes) => pickPinnedNotes(notes), setPinnedNotes),
      settle(
        repositories.medication.listOccurrences(today, today),
        (occurrences) => occurrences,
        setMedicationOccurrences,
      ),
      settle(
        repositories.notifications.listMine(10),
        (notifications) => pickCaregiverMessages(notifications),
        setCaregiverMessages,
      ),
    ])

    // Legacy detection is local-only and cheap; re-check after every load so an
    // import on the Data page clears the banner on return.
    setLegacySummary(detectLegacyLocalData())
    setRefreshedAt(new Date())
  }, [])

  useEffect(() => {
    void load(false)
  }, [load])

  // Refresh when the page regains focus/visibility (spec §8.1: data updates
  // after Planner and Health changes made elsewhere).
  useEffect(() => {
    const onFocus = () => {
      void load(true)
    }
    const onVisibility = () => {
      if (document.visibilityState === 'visible') void load(true)
    }
    window.addEventListener('focus', onFocus)
    document.addEventListener('visibilitychange', onVisibility)
    return () => {
      window.removeEventListener('focus', onFocus)
      document.removeEventListener('visibilitychange', onVisibility)
    }
  }, [load])

  const retry = useCallback(() => load(false), [load])

  return {
    online,
    pendingWrites,
    legacySummary,
    planner,
    pinnedNotes,
    medicationOccurrences,
    caregiverMessages,
    refreshedAt,
    retry,
  }
}
